import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';

import {useCursorPagination} from '../../util/cursor-pagination/cursor-pagination-hook';
import {CursorPaginationType} from '../../util/cursor-pagination/cursor-pagination-type';
import {PaginatedResponseType} from '../api/api-type';
import {useApiHooks} from '../api-hook/api-hook';
import {UseHookType} from '../api-hook/api-hook-type';
import {CatalogNameType, CclStatusEnum} from '../ccl-statistic/ccl-statistic-type';
import {useDebounce} from '../debounce-hook/debounce-hook';

import {fetchCclData, fetchCompanyCatalogSyncStatistic} from './ccl-api';
import {getFetchCclUrl} from './ccl-api-helper';
import {CclCompanyStatsType, CclDataType, CclFetchParametersType} from './ccl-type';

// eslint-disable-next-line max-params,sonarjs/cognitive-complexity,complexity
export function useCclData(
    mainFilterKey: string,
    catalogType: CatalogNameType,
    statusType: CclStatusEnum | null,
    isNewCompanies: boolean,
    sourceIdList: Array<string>,
    companyIdList?: Array<string>,
    q?: string,
    accessTransferRequired?: boolean
): UseHookType<PaginatedResponseType<CclDataType>> &
    CursorPaginationType & {setCollectionUrl: Dispatch<SetStateAction<Array<string>>>} {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<PaginatedResponseType<CclDataType>>();

    const paginationDependencies = useMemo(() => {
        return {
            q,
            mainFilterKey,
            catalogType,
            statusType,
            sourceIdList,
            companyIdList,
            isNewCompanies,
            accessTransferRequired,
        };
    }, [
        catalogType,
        companyIdList,
        mainFilterKey,
        q,
        sourceIdList,
        statusType,
        isNewCompanies,
        accessTransferRequired,
    ]);

    const [collectionUrl, setCollectionUrl] = useState<Array<string>>([]);
    const [hasMoreAfterLoaded, setHasMoreAfterLoaded] = useState<boolean>(false);

    const pagination = useCursorPagination({
        dependencies: paginationDependencies,
        shouldSaveState: false,
    });

    const {pageSize, onDataLoaded, onDataLoadFailed, refresh, page, nextPage, previousPage, resultPage} = pagination;

    const parameters: CclFetchParametersType = {
        catalogType,
        status: statusType || (accessTransferRequired && 'access_transfer_required') || null,
        catalogIds: sourceIdList.join(','),
        companyIds: companyIdList || [],
        newCompanies: isNewCompanies,
        filterKey: companyIdList?.length ? null : mainFilterKey,
        count: pageSize,
        page,
        q,
    };

    const lastPage = result?.results?.length && Math.ceil(result?.results?.length / pageSize);

    // Pages already loaded are known to have a successor; for the last loaded one only the probe knows.
    const hasNextPage = (lastPage ? resultPage < lastPage : false) || hasMoreAfterLoaded;

    const shouldUseCpEndpoint =
        (companyIdList || []).length === 0 &&
        !statusType &&
        !accessTransferRequired &&
        sourceIdList.length === 0 &&
        catalogType === 'map';

    useEffect(() => {
        setCollectionUrl([]);
        setHasMoreAfterLoaded(false);
        refresh();
        reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pageSize,
        mainFilterKey,
        shouldUseCpEndpoint,
        catalogType,
        sourceIdList,
        companyIdList,
        isNewCompanies,
        accessTransferRequired,
        statusType,
    ]);

    // The CCL endpoint skips the expensive COUNT query: it answers with a constant `count` and always
    // exposes a `next` link, so neither field tells us where the list ends. A page shorter than the page
    // size already proves it is the last one; only a page filled exactly to the page size is ambiguous,
    // and that is resolved by asking for the single row right after the ones already loaded.
    function probeNextPage(pageLength: number, loadedLength: number): void {
        if (pageLength < pageSize) {
            setHasMoreAfterLoaded(false);
            return;
        }

        // Assume no successor until the probe proves otherwise, so a stale `true` cannot open an empty page.
        setHasMoreAfterLoaded(false);

        fetchCclData(getFetchCclUrl(shouldUseCpEndpoint, {...parameters, count: 1, page: loadedLength + 1}))
            .then((probeResponse) => setHasMoreAfterLoaded(probeResponse.results.length > 0))
            .catch(() => setHasMoreAfterLoaded(false));
    }

    const fetchCclDataCallback = useCallback(() => {
        if (previousPage < nextPage && !collectionUrl.includes(getFetchCclUrl(shouldUseCpEndpoint, parameters))) {
            fetchCclData(getFetchCclUrl(shouldUseCpEndpoint, parameters))
                .then((response) => {
                    onDataLoaded(response);
                    setCollectionUrl((previousUrl) => [
                        ...previousUrl,
                        getFetchCclUrl(shouldUseCpEndpoint, parameters),
                    ]);

                    const loadedResults = result?.results
                        ? [...result.results, ...response.results]
                        : [...response.results];

                    setResult({...response, results: loadedResults});
                    probeNextPage(response.results.length, loadedResults.length);
                })
                .finally(() => setIsInProgress(false))
                .catch((error: Error) => {
                    console.error(error);
                    setProcessError(error);
                    onDataLoadFailed();
                });
        } else {
            setIsInProgress(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        setResult,
        onDataLoaded,
        setIsInProgress,
        collectionUrl,
        mainFilterKey,
        setProcessError,
        isNewCompanies,
        accessTransferRequired,
        onDataLoadFailed,
    ]);

    const beforeFetchCclData = useCallback(() => {
        if (previousPage < nextPage && !collectionUrl.includes(getFetchCclUrl(shouldUseCpEndpoint, parameters))) {
            setIsInProgress(true);
        }

        setProcessError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsInProgress, previousPage, nextPage, collectionUrl, setProcessError]);

    useDebounce(beforeFetchCclData, fetchCclDataCallback);

    return {isInProgress, processError, result, reset, pagination, lastPage, hasNextPage, setCollectionUrl};
}

export function useCclCompanyStats(companyId: number): Omit<UseHookType<Array<CclCompanyStatsType>>, 'reset'> {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult} =
        useApiHooks<Array<CclCompanyStatsType>>();

    const loadCclCompanyStats = useCallback(() => {
        fetchCompanyCatalogSyncStatistic(companyId)
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [companyId, setIsInProgress, setProcessError, setResult]);

    useEffect(() => {
        loadCclCompanyStats();
    }, [loadCclCompanyStats]);

    return {isInProgress, processError, result};
}
